import { Link } from 'react-router-dom'
import { useBedTypes, useOrganizationSettings } from '../api/hooks'
import { useAuth } from '../auth/AuthContext'
import { CensusPanel } from '../components/CensusPanel'

/**
 * The occupancy ledger: beds filling and emptying, day to day.
 *
 * @remarks
 * <para>
 * This screen used to be three things — the PPD mode switch, the bed-type editor and the
 * ledger — on the reasoning that none is usable without the others and somebody setting up
 * for the first time works straight down the page.
 * </para>
 * <para>
 * That reasoning was sound about the FIRST day and wrong about every day after it. Mode
 * and bed types are set once per installation; the ledger is touched constantly. Burying
 * an editor behind an operational screen is why the bed-type editor was reported as
 * missing when it had been built and shipped, so the setup now lives under
 * <c>/settings/occupancy</c> and this screen is only the ledger.
 * </para>
 * <para>
 * What is kept from the old arrangement is the hand-off: when there is nothing to record
 * against, this says so and points at the page that fixes it, rather than showing an empty
 * table and leaving the reader to work out why.
 * </para>
 */
export function Occupancy() {
  const { hasRole } = useAuth()
  const isAdmin = hasRole('Admin')

  const { data: settings, isLoading } = useOrganizationSettings()
  const ppdEnabled = settings?.ppdModeEnabled ?? false

  const { data: bedTypes = [] } = useBedTypes(ppdEnabled)

  if (isLoading) {
    return <p className="px-4 py-3 text-[12px]">Loading…</p>
  }

  if (!ppdEnabled) {
    return (
      <Prompt>
        PPD mode is off, so there is no occupancy to record.{' '}
        {isAdmin ? (
          <>
            Turn it on under{' '}
            <SettingsLink /> to record occupancy and report cost per patient day.
          </>
        ) : (
          'An administrator can turn it on.'
        )}
      </Prompt>
    )
  }

  // Bed types before beds: occupancy is recorded per bed type, so an empty ledger with no
  // bed types is not an empty ledger — it is a setup that has not finished.
  if (bedTypes.length === 0) {
    return (
      <Prompt>
        No bed types yet. Occupancy is recorded per bed type, so at least one is needed
        before anything can be entered.{' '}
        {isAdmin ? (
          <>
            Add one under <SettingsLink />.
          </>
        ) : (
          'An administrator can add one.'
        )}
      </Prompt>
    )
  }

  return (
    <div className="flex h-full flex-col overflow-auto">
      <CensusPanel />
    </div>
  )
}

function SettingsLink() {
  return (
    <Link
      to="/settings/occupancy"
      className="underline"
      style={{ color: 'var(--color-accent)' }}
    >
      Settings → Occupancy &amp; bed types
    </Link>
  )
}

function Prompt({ children }: { children: React.ReactNode }) {
  return (
    <div className="flex h-full items-start px-4 py-6">
      <p className="max-w-xl text-[12px]" style={{ color: 'var(--color-muted)' }}>
        {children}
      </p>
    </div>
  )
}
